import React, { useState, useMemo } from "react";
import PageLoader from "../components/ui/PageLoader";
import "../assets/css/style.css";
import { FiEdit2, FiTrash2, FiUserCheck, FiUserX } from "react-icons/fi";
import moment from "moment-timezone";
import Modal from "../components/ui/Modal";
import Button from "components/ui/Button";
import {
  useGetContractorQuery,
  useDeleteContractorMutation,
  useUpdateContractorMutation,
} from "../apis/contractor";
import { useContractorCompaniesQuery } from "apis/contractorCompany";
import ContractorView from "components/contractor/ContractorView";
import ContractorEdit from "components/contractor/ContractorEdit";
import Table from "components/ui/Table";

const Contractors = () => {
  const [viewMode, setViewMode] = useState("active"); // "active" or "deleted"
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isRestoreOpen, setIsRestoreOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [contractorToDelete, setContractorToDelete] = useState(null);
  const [contractorToRestore, setContractorToRestore] = useState(null);

  const pageSize = 10;

  const { mutate: deleteUser, isLoading: isDeleteLoading } =
    useDeleteContractorMutation();
  const { mutate: updateContractor, isLoading: isUpdateLoading } =
    useUpdateContractorMutation();

  const { data, isLoading } = useGetContractorQuery();

  // Companies fetched once here and passed down to View/Edit as a prop so
  // neither child refetches. Also used to resolve the company name per row.
  const { data: companies = [] } = useContractorCompaniesQuery("active");

  const companyNameById = useMemo(() => {
    const map = {};
    companies.forEach((c) => {
      map[c._id] = c.name;
    });
    return map;
  }, [companies]);

  // Filter data based on viewMode + flatten company name (for cell + filter).
  const filteredData = useMemo(() => {
    if (!data) return [];
    const withCompany = data.map((c) => {
      const cid = c.companyId?._id || c.companyId;
      return { ...c, companyName: companyNameById[cid] || "" };
    });
    return viewMode === "active"
      ? withCompany.filter((c) => !c.isDeleted)
      : withCompany.filter((c) => c.isDeleted);
  }, [data, viewMode, companyNameById]);

  // ─── Handlers ───
  const handleDelete = () => {
    if (contractorToDelete) {
      deleteUser(contractorToDelete._id);
      setIsDeleteOpen(false);
      setContractorToDelete(null);
    }
  };

  const handleRestore = () => {
    if (!contractorToRestore) return;
    updateContractor(
      {
        id: contractorToRestore._id,
        isDeleted: false,
        deletedAt: null,
      },
      {
        onSuccess: () => {
          setIsRestoreOpen(false);
          setContractorToRestore(null);
        },
      },
    );
  };

  const openDeleteModal = (contractor) => {
    setContractorToDelete(contractor);
    setIsDeleteOpen(true);
  };

  const openRestoreModal = (contractor) => {
    setContractorToRestore(contractor);
    setIsRestoreOpen(true);
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "index",
        header: "#",
        cell: (_, rowIndex, currentPage) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
        isFilter: false,
        isSortable: false,
      },
      {
        accessorKey: "name",
        header: "Name",
        isFilter: true,
      },
      {
        accessorKey: "calendarColor",
        header: "Color",
        cell: (row) => (
          <div
            className="w-6 h-6 rounded"
            style={{ backgroundColor: row.calendarColor }}
          />
        ),
        isFilter: false,
        isSortable: false,
      },
      {
        accessorKey: "email",
        header: "Email",
        cell: (row) => row.email || "N/A",
        isFilter: true,
      },
      {
        accessorKey: "companyName",
        header: "Company",
        cell: (row) => row.companyName || "N/A",
        isFilter: true,
      },
      {
        accessorKey: "mobile",
        header: "Mobile",
        cell: (row) => row.mobile || "N/A",
        isFilter: false,
      },
      // Deleted-only column
      ...(viewMode === "deleted"
        ? [
            {
              accessorKey: "deletedAt",
              header: "Deleted Date",
              cell: (row) =>
                row.deletedAt
                  ? moment(row.deletedAt)
                      .tz("Australia/Sydney")
                      .format("D MMM YYYY")
                  : "-",
              isFilter: false,
            },
          ]
        : []),
      {
        accessorKey: "actions",
        header: "Actions",
        cell: (row) => (
          <div className="flex items-center space-x-4">
            <button
              onClick={() => {
                setSelectedId(row._id);
                setIsEditOpen(true);
              }}
              className="edit-icon"
              title="Edit Contractor"
            >
              <FiEdit2 fontSize={20} />
            </button>
            {viewMode === "active" ? (
              <button
                onClick={() => openDeleteModal(row)}
                className="delete-icon"
                title="Delete Contractor"
              >
                <FiTrash2 fontSize={20} />
              </button>
            ) : (
              <button
                onClick={() => openRestoreModal(row)}
                className="view-icon"
                title="Restore Contractor"
              >
                <FiUserCheck fontSize={20} color="green" />
              </button>
            )}
          </div>
        ),
        isFilter: false,
        isSortable: false,
        stopPropagation: true,
        minWidth: "120px",
      },
    ],
    [viewMode],
  );

  if (isLoading) return <PageLoader />;

  const handleViewRowClick = (row) => {
    setSelectedId(row._id);
    setIsViewOpen(true);
  };

  const selectedContractor = data?.find((u) => u._id === selectedId);

  return (
    <>
      {/* View Modal */}
      <Modal
        isOpen={isViewOpen}
        onClose={() => setIsViewOpen(false)}
        title="Contractor Details"
        className="max-w-3xl"
      >
        <ContractorView user={selectedContractor} companies={companies} />
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        className="max-w-2xl"
      >
        <ContractorEdit
          user={selectedContractor}
          selectedId={selectedId}
          setIsEditOpen={setIsEditOpen}
          companies={companies}
        />
      </Modal>

      {/* Delete Confirmation */}
      <Modal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        title="Delete Contractor"
      >
        <div>
          Are you sure you want to delete contractor "{contractorToDelete?.name}
          "?
          <div className="w-full flex gap-2 justify-end py-4">
            <Button
              onClick={() => setIsDeleteOpen(false)}
              className="gray-button"
              disabled={isDeleteLoading}
            >
              No
            </Button>
            <Button
              onClick={handleDelete}
              className="black-button"
              disabled={isDeleteLoading}
              loading={isDeleteLoading}
            >
              Yes
            </Button>
          </div>
        </div>
      </Modal>

      {/* Restore Confirmation */}
      <Modal
        isOpen={isRestoreOpen}
        onClose={() => setIsRestoreOpen(false)}
        title="Restore Contractor"
      >
        <div>
          Are you sure you want to restore contractor "
          {contractorToRestore?.name}"?
          <div className="w-full flex gap-2 justify-end py-4">
            <Button
              onClick={() => setIsRestoreOpen(false)}
              className="gray-button"
              disabled={isUpdateLoading}
            >
              No
            </Button>
            <Button
              onClick={handleRestore}
              className="black-button"
              disabled={isUpdateLoading}
              loading={isUpdateLoading}
            >
              Yes
            </Button>
          </div>
        </div>
      </Modal>

      {/* Page content */}
      <div className="container mx-auto">
        {/* Toggle (same look as Users page) */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex gap-2 bg-lightgray rounded-lg p-1 border border-mediumgray">
            <button
              onClick={() => setViewMode("active")}
              className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                viewMode === "active"
                  ? "bg-white text-black shadow-sm"
                  : "text-darkgray hover:text-black"
              }`}
            >
              <FiUserCheck className="w-4 h-4" />
              <span>Active Contractors</span>
            </button>
            <button
              onClick={() => setViewMode("deleted")}
              className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                viewMode === "deleted"
                  ? "bg-white text-black shadow-sm"
                  : "text-darkgray hover:text-black"
              }`}
            >
              <FiUserX className="w-4 h-4" />
              <span>Deleted Contractors</span>
            </button>
          </div>
        </div>

        <Table
          title="Contractors"
          columns={columns}
          data={filteredData}
          pageSize={pageSize}
          onRowClick={handleViewRowClick}
        />
      </div>
    </>
  );
};

export default Contractors;
